import React, { useEffect, useRef, useState } from 'react'
import { motion, useScroll, useTransform, useInView, AnimatePresence } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Play, Pause, Volume2, VolumeX } from 'lucide-react'
import { Navbar, Footer } from '../components/SharedLayout'

// Premium easing
const premiumEase = [0.16, 1, 0.3, 1] as const

// Brand Colors
const colors = {
  teal: '#145F6B',
  tealDark: '#083B3A',
  copper: '#9B775D',
  copperLight: '#AB9381',
  cream: '#F6F6F1',
  warmCream: 'rgb(246, 241, 237)',
  nearBlack: '#0F0F0F',
  white: '#FFFFFF',
}

// Image Assets
const images = {
  logo: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/new-website-images/hudson-stacked-dark.png',
  logoLight: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/site-images/HMH_Secondary_Logo_Tape%20Turquoise.png',
  hero: '/hero-image.png',
  pattern: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/new-website-images/huson-pattern-landscape.png',
  hudsonDifference: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/new-website-images/section.png',
  familyHugging: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/new-website-images/familyhugging.png',
  curtainCleaning: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/new-website-images/curtaincleaning.png',
  hudson1: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/new-website-images/hudson1.jpg',
  hudson2: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/new-website-images/hudson2.jpg',
  hudson3: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/new-website-images/hudson3.jpg',
  testimonials: {
    taylor: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/new-website-images/taylor-g-testimonial.png',
    bailey: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/new-website-images/bailey-p-testimonial.png',
    kelsey: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/new-website-images/kelsey-s-testimonial.png',
  }
}

// Video Assets
const videos = {
  testimonial1: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/site-images/c3a79041-1dff-4d4d-8a11-1aba67bf4036.mp4',
  testimonial2: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/site-images/3c99f9af-8208-4eff-884a-53d2f969b784.mp4',
  testimonial3: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/site-images/fd242a71-1ebc-46b3-84da-a8c8e9d4f860.mp4',
}

// ============================================
// HERO - With background image
// ============================================
const HeroSection: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  })
  
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0])
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 0.95])
  const y = useTransform(scrollYProgress, [0, 0.5], [0, 100])

  return (
    <section ref={containerRef} className="relative min-h-[100vh] flex items-center overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url('${images.hero}')`, backgroundPosition: '50% 15%' }}
      />
      {/* Overlay */}
      <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(15,15,15,0.4), rgba(15,15,15,0.7))' }} />
      
      <motion.div 
        style={{ opacity, scale, y }}
        className="relative w-full max-w-[1400px] mx-auto px-6 lg:px-12 pt-32 pb-24 lg:py-48"
      >
        <div className="max-w-3xl">
          {/* Label */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: premiumEase }}
          >
            <span className="label-uppercase flex items-center gap-3 text-white/80">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: colors.copper }} />
              Home Management Reimagined
            </span>
          </motion.div>
          
          {/* Headline */}
          <div className="overflow-hidden mt-6">
            <motion.h1
              initial={{ y: 100 }}
              animate={{ y: 0 }}
              transition={{ duration: 1, ease: premiumEase, delay: 0.1 }}
              className="font-serif text-white"
              style={{ 
                fontSize: 'clamp(3rem, 8vw, 6rem)', 
                lineHeight: 1.15, 
                letterSpacing: '-0.03em',
              }}
            >
              We maintain your home
              <br />
              <span className="italic" style={{ color: colors.copper }}>so you don&apos;t have to.</span>
            </motion.h1>
          </div>
          
          {/* Subtext - confidence, be informed not involved */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: premiumEase, delay: 0.3 }}
            className="mt-8 max-w-xl text-white/80"
            style={{ fontSize: '18px', lineHeight: 1.7 }}
          >
            We handle maintenance, coordinate vendors, and keep everything running.
            Be informed, not involved—so you can live more and worry less.
          </motion.p>
          
          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: premiumEase, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 mt-10"
          >
            <a
              href="https://apps.apple.com/app/hudson-my-home"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center justify-center gap-3 text-white px-8 py-4 rounded-full text-base font-medium transition-all duration-400 hover:opacity-90"
              style={{ backgroundColor: colors.copper }}
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
              <span>Download for iOS</span>
              <span className="group-hover:translate-x-1 transition-transform duration-300">→</span>
            </a>
            
            <a
              href="https://play.google.com/store/apps/details?id=com.hudsonmyhome"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full text-base font-medium border-2 border-white/30 text-white transition-all duration-400 hover:bg-white/10"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 20.5v-17c0-.59.34-1.11.84-1.35L13.69 12l-9.85 9.85c-.5-.24-.84-.76-.84-1.35m13.81-5.38L6.05 21.34l8.49-8.49 2.27 2.27m3.35-4.31c.34.27.59.69.59 1.19s-.22.9-.57 1.18l-2.29 1.32-2.5-2.5 2.5-2.5 2.27 1.31M6.05 2.66l10.76 6.22-2.27 2.27-8.49-8.49z"/>
              </svg>
              <span>Get on Android</span>
            </a>
          </motion.div>
          
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, ease: premiumEase, delay: 0.6 }}
            className="mt-4 text-sm text-white/60"
          >
            30-day risk-free trial • No credit card required
          </motion.p>
        </div>
      </motion.div>
      
      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2"
        >
          <div className="w-1.5 h-1.5 rounded-full bg-white/50" />
        </motion.div>
      </motion.div>
    </section>
  )
}

// ============================================
// FEATURES SECTION with Images
// ============================================
const FeaturesSection: React.FC = () => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })
  
  const features = [
    {
      icon: "🎯",
      title: "Proactive Care",
      description: "Hudson anticipates your home's needs before problems arise, scheduling maintenance at the optimal time."
    },
    {
      icon: "🤝",
      title: "Vetted Vendors",
      description: "Access our network of licensed, insured, and background-checked professionals—all pre-negotiated rates."
    },
    {
      icon: "📱",
      title: "One App Control",
      description: "Manage everything from your pocket. Approve work, track progress, and stay in the loop in real-time."
    },
    {
      icon: "🔔",
      title: "Smart Reminders",
      description: "Never miss a filter change, seasonal prep, or warranty deadline. Hudson keeps you informed."
    },
    {
      icon: "🏠",
      title: "Home History",
      description: "Every repair, upgrade, and maintenance task documented. Perfect for insurance or resale value."
    }
  ]
  
  return (
    <section id="features" className="py-32 lg:py-48 bg-white">
      <div ref={ref} className="max-w-[1400px] mx-auto px-6 lg:px-12">
        {/* Section Header with Image */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 mb-20 lg:mb-32">
          <div>
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, ease: premiumEase }}
              className="label-uppercase block mb-6"
              style={{ color: colors.teal }}
            >
              Features
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, ease: premiumEase, delay: 0.1 }}
              className="font-serif fluid-section-title"
              style={{ color: colors.nearBlack }}
            >
              Everything your home needs, 
              <span className="italic" style={{ color: colors.copper }}> nothing it doesn't.</span>
            </motion.h2>
          </div>
          
          {/* Feature Image */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, ease: premiumEase, delay: 0.2 }}
            className="relative aspect-[4/3] rounded-2xl overflow-hidden"
          >
            <img 
              src={images.familyHugging} 
              alt="Family enjoying their home" 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
          </motion.div>
        </div>
        
        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ 
                duration: 0.8, 
                ease: premiumEase, 
                delay: 0.3 + index * 0.1 
              }}
              className="group"
            >
              <div className="mb-6 text-4xl">{feature.icon}</div>
              <h3 
                className="font-serif text-2xl mb-4 transition-colors duration-300"
                style={{ color: colors.nearBlack }}
              >
                {feature.title}
              </h3>
              <p className="leading-relaxed" style={{ color: '#525252' }}>
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ============================================
// HOW IT WORKS - With lifestyle image
// ============================================
const HowItWorksSection: React.FC = () => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })
  
  const steps = [
    {
      number: "01",
      title: "Download the App",
      description: "Get Hudson on your iPhone or Android. Create your account in under 2 minutes."
    },
    {
      number: "02", 
      title: "Tell Us About Your Home",
      description: "Share your home's details—size, age, systems. Hudson builds your personalized care plan."
    },
    {
      number: "03",
      title: "Sit Back & Relax",
      description: "Hudson handles the rest. Scheduling, coordination, quality checks—all managed for you."
    }
  ]
  
  return (
    <section id="how-it-works" className="py-32 lg:py-48" style={{ backgroundColor: colors.cream }}>
      <div ref={ref} className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-16 lg:gap-8">
          {/* Left Column - Title + Image */}
          <div className="lg:col-span-5 lg:sticky lg:top-32 lg:self-start space-y-8">
            <div>
              <motion.span
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, ease: premiumEase }}
                className="label-uppercase block mb-6"
                style={{ color: colors.teal }}
              >
                How It Works
              </motion.span>
              <motion.h2
                initial={{ opacity: 0, y: 40 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, ease: premiumEase, delay: 0.1 }}
                className="font-serif fluid-subheadline mb-8"
                style={{ color: colors.nearBlack }}
              >
                Three steps to a 
                <span className="italic" style={{ color: colors.copper }}> worry-free</span> home.
              </motion.h2>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, ease: premiumEase, delay: 0.2 }}
              >
                <a
                  href="https://apps.apple.com/app/hudson-my-home"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 font-medium group"
                  style={{ color: colors.teal }}
                >
                  <span className="link-underline">Get started free</span>
                  <span className="group-hover:translate-x-2 transition-transform duration-300">→</span>
                </a>
              </motion.div>
            </div>
            
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, ease: premiumEase, delay: 0.3 }}
              className="hidden lg:block rounded-2xl overflow-hidden aspect-[3/4]"
            >
              <img 
                src={images.curtainCleaning}
                alt="Professional home care"
                className="w-full h-full object-cover"
              />
            </motion.div>
          </div>
          
          {/* Right Column - Steps */}
          <div className="lg:col-span-7 space-y-16 lg:space-y-24">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 60 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ 
                  duration: 0.8, 
                  ease: premiumEase, 
                  delay: 0.3 + index * 0.15 
                }}
                className="relative pl-20 lg:pl-28"
              >
                <span 
                  className="absolute left-0 top-0 font-serif text-6xl lg:text-8xl leading-none"
                  style={{ color: `${colors.teal}20` }}
                >
                  {step.number}
                </span>
                <h3 
                  className="font-serif text-3xl lg:text-4xl mb-4"
                  style={{ color: colors.nearBlack }}
                >
                  {step.title}
                </h3>
                <p className="text-lg leading-relaxed max-w-md" style={{ color: '#525252' }}>
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

// ============================================
// VIDEO TESTIMONIALS SECTION
// ============================================
interface VideoCardProps {
  videoUrl: string
  name: string
  location: string
  quote: string
}

const VideoCard: React.FC<VideoCardProps> = ({ videoUrl, name, location, quote }) => {
  const [isPlaying, setIsPlaying] = useState(false)
  const [isMuted, setIsMuted] = useState(true)
  const videoRef = useRef<HTMLVideoElement>(null)

  const handlePlayPause = async () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause()
        setIsPlaying(false)
      } else {
        try {
          await videoRef.current.play()
          setIsPlaying(true)
        } catch (error) {
          console.error('Error playing video:', error)
        }
      }
    }
  }

  return (
    <div 
      className="relative rounded-2xl overflow-hidden group cursor-pointer"
      style={{ aspectRatio: '9/16', minHeight: '400px' }}
      onClick={handlePlayPause}
    >
      <video
        ref={videoRef}
        className="w-full h-full object-cover"
        muted={isMuted}
        playsInline
        loop
        preload="metadata"
      >
        <source src={videoUrl} type="video/mp4" />
      </video>

      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />

      {/* Play Button */}
      <div className="absolute inset-0 flex items-center justify-center">
        <button
          className={`w-16 h-16 rounded-full backdrop-blur-sm flex items-center justify-center transition-all duration-300 ${
            isPlaying ? 'bg-white/20 opacity-0 group-hover:opacity-100' : 'bg-white/30'
          }`}
          onClick={(e) => { e.stopPropagation(); handlePlayPause() }}
        >
          {isPlaying ? (
            <Pause className="w-8 h-8 text-white" />
          ) : (
            <Play className="w-8 h-8 text-white ml-1" />
          )}
        </button>
      </div>

      {/* Volume Control */}
      <button
        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={(e) => {
          e.stopPropagation()
          if (videoRef.current) {
            videoRef.current.muted = !isMuted
            setIsMuted(!isMuted)
          }
        }}
      >
        {isMuted ? (
          <VolumeX className="w-5 h-5 text-white" />
        ) : (
          <Volume2 className="w-5 h-5 text-white" />
        )}
      </button>

      {/* Info */}
      <div className="absolute bottom-0 left-0 right-0 p-6">
        <h3 className="text-white font-semibold text-lg mb-1">{name}</h3>
        <p className="text-white/70 text-sm mb-3">{location}</p>
        <p className="text-white/90 text-sm leading-relaxed">{quote}</p>
      </div>
    </div>
  )
}

const VideoTestimonialsSection: React.FC = () => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })
  
  const testimonials = [
    {
      videoUrl: videos.testimonial1,
      name: "Lisa Anderson",
      location: "Urbandale Homeowner",
      quote: "Hudson's proactive approach has saved me thousands in emergency repairs."
    },
    {
      videoUrl: videos.testimonial2,
      name: "Keisha Williams",
      location: "West Des Moines Resident",
      quote: "The peace of mind Hudson provides is priceless."
    },
    {
      videoUrl: videos.testimonial3,
      name: "James Martinez",
      location: "Waukee Homeowner",
      quote: "They treat my home like it's their own."
    }
  ]

  return (
    <section id="testimonials" className="py-32 lg:py-48 bg-white">
      <div ref={ref} className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="text-center mb-16">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, ease: premiumEase }}
            className="label-uppercase block mb-6"
            style={{ color: colors.teal }}
          >
            Member Stories
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, ease: premiumEase, delay: 0.1 }}
            className="font-serif fluid-subheadline"
            style={{ color: colors.nearBlack }}
          >
            See what our <span className="italic" style={{ color: colors.copper }}>members</span> say.
          </motion.h2>
        </div>
        
        <div className="grid md:grid-cols-3 gap-6">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.name}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ 
                duration: 0.8, 
                ease: premiumEase, 
                delay: 0.2 + index * 0.1 
              }}
            >
              <VideoCard {...testimonial} />
            </motion.div>
          ))}
        </div>
        
        <motion.p
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-center mt-8 text-sm"
          style={{ color: '#737373' }}
        >
          Tap any video to play • All videos muted by default
        </motion.p>
      </div>
    </section>
  )
}

// ============================================
// DARK CONTRAST SECTION with Hudson Image
// ============================================
const DarkSection: React.FC = () => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  })
  
  const bgTextX = useTransform(scrollYProgress, [0, 1], [0, -200])
  
  return (
    <section ref={ref} className="relative py-32 lg:py-48 overflow-hidden" style={{ backgroundColor: colors.tealDark }}>
      {/* Large Background Text */}
      <motion.div 
        style={{ x: bgTextX }}
        className="absolute top-1/2 -translate-y-1/2 left-0 whitespace-nowrap pointer-events-none"
      >
        <span 
          className="font-serif select-none"
          style={{ 
            fontSize: 'clamp(8rem, 25vw, 20rem)', 
            letterSpacing: '-0.05em',
            color: `${colors.teal}30`
          }}
        >
          Live More. Worry Less.
        </span>
      </motion.div>
      
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, ease: premiumEase }}
            className="space-y-12"
          >
            <div>
              <span className="label-uppercase block mb-6" style={{ color: colors.copper }}>The Hudson Difference</span>
              <h2 className="font-serif text-white text-4xl lg:text-5xl leading-tight mb-6">
                Others maintain.
                <br />
                <span className="italic" style={{ color: colors.copper }}>We anticipate.</span>
              </h2>
              <p className="text-lg text-white/70 max-w-md">
                Our members don't just get service — they join a standard of care so high, they'll never go back.
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-8">
              <div>
                <div className="font-serif text-white text-5xl lg:text-6xl mb-2">98%</div>
                <p style={{ color: colors.copperLight }}>Member satisfaction rate</p>
              </div>
              <div>
                <div className="font-serif text-white text-5xl lg:text-6xl mb-2">15+</div>
                <p style={{ color: colors.copperLight }}>Hours saved monthly</p>
              </div>
            </div>
          </motion.div>
          
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, ease: premiumEase, delay: 0.2 }}
            className="relative"
          >
            <div className="rounded-2xl overflow-hidden aspect-[3/4] max-w-md mx-auto lg:ml-auto">
              <img 
                src={images.hudsonDifference}
                alt="The Hudson Difference"
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

// ============================================
// WRITTEN TESTIMONIALS with Real Photos
// ============================================
const WrittenTestimonialsSection: React.FC = () => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })
  
  const testimonials = [
    {
      name: "Taylor G.",
      location: "Ankeny, IA",
      quote: "Hudson saved us from a major HVAC failure — before it happened. The peace of mind is priceless.",
      image: images.testimonials.taylor
    },
    {
      name: "Bailey P.",
      location: "Ankeny, IA",
      quote: "We finally have our weekends back. Joining Hudson My Home was the smartest decision we've made for our home.",
      image: images.testimonials.bailey
    },
    {
      name: "Kelsey S.",
      location: "Ankeny, IA",
      quote: "The level of anticipation is extraordinary. They know our home better than we do and handle everything before we even think of it.",
      image: images.testimonials.kelsey
    }
  ]

  return (
    <section className="py-32 lg:py-48" style={{ backgroundColor: colors.warmCream }}>
      <div ref={ref} className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: premiumEase }}
          className="text-center mb-16"
        >
          <h2 className="font-serif text-4xl lg:text-5xl" style={{ color: colors.nearBlack }}>
            Yes, It Really Works
          </h2>
        </motion.div>

        <div className="space-y-12">
          {testimonials.map((t, idx) => (
            <motion.div
              key={t.name}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, ease: premiumEase, delay: 0.1 + idx * 0.1 }}
            >
              {idx > 0 && (
                <div className="mb-12" style={{ height: 1, backgroundColor: 'rgba(15,15,15,0.15)' }} />
              )}
              <div className="grid grid-cols-1 md:grid-cols-[300px_1fr] gap-8 items-start">
                <div className="w-full max-w-[300px] aspect-[310/277] overflow-hidden rounded-xl mx-auto md:mx-0">
                  <img src={t.image} alt={t.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex flex-col justify-center">
                  <p className="text-sm font-medium mb-3" style={{ color: colors.nearBlack }}>
                    {t.name}
                  </p>
                  <p className="font-serif text-2xl md:text-3xl leading-snug mb-4" style={{ color: colors.nearBlack }}>
                    "{t.quote}"
                  </p>
                  <p className="text-sm" style={{ color: '#525252' }}>
                    {t.location}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ============================================
// CTA SECTION - Download focused
// ============================================
const CTASection: React.FC = () => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })
  
  return (
    <section 
      ref={ref} 
      className="py-32 lg:py-48 relative overflow-hidden"
      style={{ backgroundColor: colors.teal }}
    >
      {/* Pattern Background */}
      <div 
        className="absolute inset-0 opacity-10"
        style={{ 
          backgroundImage: `url('${images.pattern}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      />
      
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 text-center">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: premiumEase }}
          className="max-w-4xl mx-auto"
        >
          <span className="label-uppercase block mb-8 text-white/60">Start Today</span>
          <h2 
            className="font-serif text-white mb-8"
            style={{ 
              fontSize: 'clamp(2.5rem, 7vw, 5rem)', 
              lineHeight: 1, 
              letterSpacing: '-0.03em',
            }}
          >
            Ready for a home that <span className="italic" style={{ color: colors.copper }}>manages itself</span>?
          </h2>
          <p className="text-lg lg:text-xl mb-12 max-w-2xl mx-auto text-white/80">
            Join thousands of homeowners who've reclaimed their weekends. 
            Download the app and experience the difference.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <a
              href="https://apps.apple.com/app/hudson-my-home"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center justify-center gap-3 px-10 py-5 rounded-full text-lg font-medium transition-all duration-400 hover:scale-105"
              style={{ backgroundColor: colors.copper, color: colors.white }}
            >
              <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
              <span>App Store</span>
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.hudsonmyhome"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center justify-center gap-3 px-10 py-5 rounded-full text-lg font-medium border-2 border-white/30 text-white transition-all duration-400 hover:bg-white/10 hover:scale-105"
            >
              <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 20.5v-17c0-.59.34-1.11.84-1.35L13.69 12l-9.85 9.85c-.5-.24-.84-.76-.84-1.35m13.81-5.38L6.05 21.34l8.49-8.49 2.27 2.27m3.35-4.31c.34.27.59.69.59 1.19s-.22.9-.57 1.18l-2.29 1.32-2.5-2.5 2.5-2.5 2.27 1.31M6.05 2.66l10.76 6.22-2.27 2.27-8.49-8.49z"/>
              </svg>
              <span>Google Play</span>
            </a>
          </div>
          
          <p className="text-sm text-white/50">
            Free to download • No credit card required • Cancel anytime
          </p>
        </motion.div>
      </div>
    </section>
  )
}

// ============================================
// APP LANDING PAGE - For Ad Campaigns
// ============================================
const AppLandingPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        <HeroSection />
        <FeaturesSection />
        <HowItWorksSection />
        <VideoTestimonialsSection />
        <DarkSection />
        <WrittenTestimonialsSection />
        <CTASection />
      </main>
      <Footer />
    </div>
  )
}

export default AppLandingPage
